import React, { useEffect, useState, useContext } from 'react';
import Page from '../components/Page';
import { 
  IconTruck, 
  IconBed, 
  IconCheck, 
  IconX, 
  IconMapPin, 
  IconPhone, 
  IconClock, 
  IconUser, 
  IconCalendar, 
  IconSearch, 
  IconExternalLink,
  IconShoppingBag
} from '@tabler/icons-react';
import { iconStroke } from '../config/config';
import { 
  getDeliveryRoomRequests, 
  acceptDeliveryRoomRequest, 
  declineDeliveryRoomRequest 
} from '../controllers/pos.controller';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { SocketContext } from '../contexts/SocketContext';
import { getUserDetailsInLocalStorage } from '../helpers/UserDetails';

export default function DeliveryRoomRequestsPage() {
  const { t } = useTranslation();
  const { socket } = useContext(SocketContext);
  const user = getUserDetailsInLocalStorage();
  const tenantId = user?.tenant_id;

  const [activeTab, setActiveTab] = useState('pending');
  const [loading, setLoading] = useState(true);
  const [pendingRequests, setPendingRequests] = useState([]);
  const [acceptedRequests, setAcceptedRequests] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const _fetchRequests = async (showToast = false) => {
    try {
      if (showToast) toast.loading("Fetching requests...");
      const res = await getDeliveryRoomRequests();
      if (res.status === 200) {
        setPendingRequests(res.data.pending || []);
        setAcceptedRequests(res.data.acceptedToday || []);
      }
    } catch (error) {
      console.error(error);
      toast.error("Failed to load requests");
    } finally {
      setLoading(false);
      toast.dismiss();
    }
  };

  useEffect(() => {
    _fetchRequests();
  }, []);

  useEffect(() => {
    if (socket && tenantId) {
      socket.emit("authenticate", tenantId);

      const handleUpdate = () => {
        _fetchRequests();
      };

      socket.on("new_qrorder", handleUpdate);
      socket.on("order_update", handleUpdate);
      socket.on("new_booking", handleUpdate);

      return () => {
        socket.off("new_qrorder", handleUpdate);
        socket.off("order_update", handleUpdate);
        socket.off("new_booking", handleUpdate);
      };
    }
  }, [socket, tenantId]);

  const handleAccept = async (id, type) => {
    try {
      toast.loading("Accepting request...");
      const res = await acceptDeliveryRoomRequest(id, type);
      if (res.status === 200) {
        toast.dismiss();
        toast.success("Request accepted successfully");
        _fetchRequests();
      }
    } catch (error) {
      console.error(error);
      toast.dismiss();
      toast.error("Failed to accept request");
    }
  };

  const handleDecline = async (id, type) => {
    if (!window.confirm("Are you sure you want to decline this request?")) return;
    try {
      toast.loading("Declining request...");
      const res = await declineDeliveryRoomRequest(id, type);
      if (res.status === 200) {
        toast.dismiss();
        toast.success("Request declined successfully");
        _fetchRequests();
      }
    } catch (error) {
      console.error(error);
      toast.dismiss();
      toast.error("Failed to decline request");
    }
  };

  const filteredPending = pendingRequests.filter(req => {
    const q = searchQuery.toLowerCase();
    return (
      (req.customer_name && req.customer_name.toLowerCase().includes(q)) ||
      (req.guest_name && req.guest_name.toLowerCase().includes(q)) ||
      (req.customer_phone && req.customer_phone.includes(q)) ||
      (req.delivery_type && req.delivery_type.toLowerCase().includes(q)) ||
      (req.room_type_name && req.room_type_name.toLowerCase().includes(q))
    );
  });

  const filteredAccepted = acceptedRequests.filter(req => {
    const q = searchQuery.toLowerCase();
    return (
      (req.customer_name && req.customer_name.toLowerCase().includes(q)) ||
      (req.guest_name && req.guest_name.toLowerCase().includes(q)) ||
      (req.customer_phone && req.customer_phone.includes(q)) ||
      (req.delivery_type && req.delivery_type.toLowerCase().includes(q)) ||
      (req.room_type_name && req.room_type_name.toLowerCase().includes(q))
    );
  });

  const activeRequests = activeTab === 'pending' ? filteredPending : filteredAccepted;

  return (
    <Page className="px-4 py-6 w-full max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h2 className="text-3xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-green-600 to-emerald-500 dark:from-green-400 dark:to-emerald-300">
            Delivery & Room Portal
          </h2>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
            Manage incoming food delivery, room service orders, and room booking requests.
          </p>
        </div>
        
        {/* Search */}
        <div className="relative w-full md:w-80">
          <input 
            type="text" 
            placeholder="Search by customer name, phone..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-white dark:bg-zinc-900 border border-restro-border-green dark:border-zinc-800 rounded-2xl pl-10 pr-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
          />
          <IconSearch className="absolute left-3 top-2.5 text-gray-400" size={18} stroke={iconStroke} />
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 p-1 bg-gray-100 dark:bg-zinc-900 rounded-2xl mb-6 w-fit border border-restro-border-green dark:border-zinc-800">
        <button 
          onClick={() => setActiveTab('pending')}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition active:scale-95 ${
            activeTab === 'pending' 
              ? 'bg-white dark:bg-zinc-800 shadow-md text-green-600 dark:text-green-400' 
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white'
          }`}
        >
          <span>Pending Requests</span>
          {pendingRequests.length > 0 && (
            <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full font-bold animate-pulse">
              {pendingRequests.length}
            </span>
          )}
        </button>
        <button 
          onClick={() => setActiveTab('accepted')}
          className={`flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold transition active:scale-95 ${
            activeTab === 'accepted' 
              ? 'bg-white dark:bg-zinc-800 shadow-md text-green-600 dark:text-green-400' 
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-white'
          }`}
        >
          <span>Today's Accepted Orders</span>
          {acceptedRequests.length > 0 && (
            <span className="bg-green-500 text-white text-xs px-2 py-0.5 rounded-full font-bold">
              {acceptedRequests.length}
            </span>
          )}
        </button>
      </div>

      {/* List Container */}
      {loading ? (
        <div className="flex justify-center items-center h-64">
          <span className="loading loading-spinner loading-lg text-green-600"></span>
        </div>
      ) : activeRequests.length === 0 ? (
        <div className="flex flex-col items-center justify-center text-center py-16 bg-white dark:bg-zinc-950 rounded-3xl border border-dashed border-gray-300 dark:border-zinc-800 shadow-sm">
          <IconShoppingBag size={48} className="text-gray-400 dark:text-zinc-600 mb-3" stroke={1.5} />
          <p className="text-lg font-bold text-gray-700 dark:text-gray-300">No requests found</p>
          <p className="text-sm text-gray-400 mt-1">Incoming delivery, room orders or bookings will appear here.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {activeRequests.map((req) => {
            const isOrder = req.type === 'order';
            const isDelivery = isOrder && req.delivery_type === 'delivery';
            const isRoomService = isOrder && req.delivery_type === 'room_service';
            const isBooking = req.type === 'booking';

            return (
              <div 
                key={`${req.type}-${req.id}`}
                className="group relative flex flex-col bg-white dark:bg-zinc-950 border border-restro-border-green dark:border-zinc-800 rounded-3xl p-5 shadow-sm hover:shadow-lg transition-all duration-300 transform hover:-translate-y-1"
              >
                {/* Ribbon Tag / Badges */}
                <div className="flex items-center justify-between mb-4">
                  <span className={`flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold ${
                    isDelivery ? 'bg-amber-50 text-amber-600 dark:bg-amber-950/40 dark:text-amber-400 border border-amber-100 dark:border-amber-900/50' :
                    isRoomService ? 'bg-sky-50 text-sky-600 dark:bg-sky-950/40 dark:text-sky-400 border border-sky-100 dark:border-sky-900/50' :
                    'bg-indigo-50 text-indigo-600 dark:bg-indigo-950/40 dark:text-indigo-400 border border-indigo-100 dark:border-indigo-900/50'
                  }`}>
                    {isDelivery && <IconTruck size={14} />}
                    {isRoomService && <IconBed size={14} />}
                    {isBooking && <IconCalendar size={14} />}
                    {isDelivery ? 'Food Delivery' : isRoomService ? 'Room Service Order' : 'Room Booking'}
                  </span>
                  
                  <span className="text-xs text-gray-400 flex items-center gap-1">
                    <IconClock size={12} />
                    {new Date(req.created_at || req.date).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                  </span>
                </div>

                {/* ID and Date Header */}
                <h3 className="font-extrabold text-lg text-gray-800 dark:text-gray-100 flex items-center gap-1.5">
                  Request #{req.id}
                </h3>

                {/* Divider */}
                <hr className="my-3 border-gray-100 dark:border-zinc-800" />

                {/* Details list */}
                <div className="flex-1 space-y-2.5 text-sm">
                  {/* Customer details */}
                  <div className="flex items-start gap-2.5">
                    <IconUser className="text-gray-400 mt-0.5 shrink-0" size={16} />
                    <div>
                      <p className="font-semibold text-gray-800 dark:text-gray-200">
                        {req.customer_name || req.guest_name || "Guest"}
                      </p>
                      {(req.customer_phone || req.guest_phone) && (
                        <a 
                          href={`tel:${req.customer_phone || req.guest_phone}`}
                          className="text-xs text-green-600 dark:text-green-400 hover:underline flex items-center gap-1 mt-0.5"
                        >
                          <IconPhone size={12} />
                          {req.customer_phone || req.guest_phone}
                        </a>
                      )}
                      {isBooking && req.guest_email && (
                        <p className="text-xs text-gray-400 mt-0.5">{req.guest_email}</p>
                      )}
                    </div>
                  </div>

                  {/* Delivery Location / Room Details */}
                  {isDelivery && (
                    <div className="flex items-start gap-2.5 bg-gray-50 dark:bg-zinc-900/40 p-2.5 rounded-xl border border-gray-100 dark:border-zinc-800/80">
                      <IconMapPin className="text-red-500 mt-0.5 shrink-0" size={16} />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-gray-700 dark:text-gray-300 text-xs">Delivery Address</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5 break-words">
                          {req.delivery_address || "No address provided"}
                        </p>
                        {req.latitude && req.longitude ? (
                          <div className="mt-2 flex items-center gap-2">
                            <span className="text-[10px] bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-400 px-2 py-0.5 rounded font-mono">
                              GPS: {req.latitude.toFixed(5)}, {req.longitude.toFixed(5)}
                            </span>
                            <a 
                              href={`https://www.google.com/maps/search/?api=1&query=${req.latitude},${req.longitude}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="text-[11px] font-bold text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-0.5"
                            >
                              <span>View Map</span>
                              <IconExternalLink size={10} />
                            </a>
                          </div>
                        ) : (
                          <span className="text-[10px] text-gray-400 block mt-1">GPS coordinates not available</span>
                        )}
                      </div>
                    </div>
                  )}

                  {isRoomService && (
                    <div className="flex items-start gap-2.5 bg-gray-50 dark:bg-zinc-900/40 p-2.5 rounded-xl border border-gray-100 dark:border-zinc-800/80">
                      <IconBed className="text-sky-500 mt-0.5 shrink-0" size={16} />
                      <div>
                        <p className="font-semibold text-gray-700 dark:text-gray-300 text-xs">Room Service Destination</p>
                        <p className="font-bold text-sm text-sky-600 dark:text-sky-400 mt-0.5">
                          {req.source || `Room ${req.table_title || 'N/A'}`}
                        </p>
                      </div>
                    </div>
                  )}

                  {isBooking && (
                    <div className="bg-gray-50 dark:bg-zinc-900/40 p-2.5 rounded-xl border border-gray-100 dark:border-zinc-800/80 space-y-1.5">
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-400">Room Type:</span>
                        <span className="font-semibold text-gray-700 dark:text-gray-300">{req.room_type_name}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-400">Check In:</span>
                        <span className="font-semibold text-gray-700 dark:text-gray-300">{new Date(req.check_in).toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-400">Check Out:</span>
                        <span className="font-semibold text-gray-700 dark:text-gray-300">{new Date(req.check_out).toLocaleDateString()}</span>
                      </div>
                      <div className="flex justify-between text-xs">
                        <span className="text-gray-400">Adults:</span>
                        <span className="font-semibold text-gray-700 dark:text-gray-300">{req.people_count || 1}</span>
                      </div>
                      <div className="flex justify-between text-xs pt-1 border-t border-gray-200/50 dark:border-zinc-800/50">
                        <span className="text-gray-400 font-medium">Total Amount:</span>
                        <span className="font-bold text-green-600 dark:text-green-400">Rs. {Number(String(req.amount || 0).replace(/,/g, "")).toFixed(2)}</span>
                      </div>
                    </div>
                  )}

                  {/* Order Items */}
                  {isOrder && req.items && req.items.length > 0 && (
                    <div className="mt-2.5">
                      <p className="font-semibold text-gray-700 dark:text-gray-300 text-xs mb-1.5">Ordered Items:</p>
                      <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                        {req.items.map((item, idx) => (
                          <div key={idx} className="flex justify-between text-xs py-1 px-2 rounded-lg bg-gray-50/50 dark:bg-zinc-900/20 border border-gray-100/50 dark:border-zinc-800/20">
                            <div className="min-w-0 flex-1">
                              <span className="font-semibold text-gray-800 dark:text-gray-200">
                                {item.item_title}
                              </span>
                              {item.variant_title && (
                                <span className="text-[10px] text-gray-400 ml-1.5">
                                  ({item.variant_title})
                                </span>
                              )}
                              {item.notes && (
                                <p className="text-[10px] text-gray-400 italic mt-0.5">Note: {item.notes}</p>
                              )}
                            </div>
                            <span className="text-gray-500 font-medium shrink-0 ml-2">
                              x{item.quantity}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* Actions Footer */}
                {activeTab === 'pending' && (
                  <div className="mt-5 pt-4 border-t border-gray-100 dark:border-zinc-800 flex gap-3">
                    <button 
                      onClick={() => handleAccept(req.id, req.type)}
                      className="flex-1 flex items-center justify-center gap-1 bg-green-600 hover:bg-green-700 dark:bg-green-700 dark:hover:bg-green-600 text-white py-2 px-3 rounded-2xl text-xs font-bold transition active:scale-95 shadow-sm"
                    >
                      <IconCheck size={14} />
                      Accept
                    </button>
                    <button 
                      onClick={() => handleDecline(req.id, req.type)}
                      className="flex-1 flex items-center justify-center gap-1 bg-red-50 hover:bg-red-100 dark:bg-zinc-900 dark:hover:bg-red-950/40 text-red-600 dark:text-red-400 border border-red-200 dark:border-red-900/30 py-2 px-3 rounded-2xl text-xs font-bold transition active:scale-95"
                    >
                      <IconX size={14} />
                      Decline
                    </button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </Page>
  );
}
